import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppId, AppThunkAPIType } from "../../app/appTypes";
import { getWithAuth, postWithAuth, putWithAuth, deleteWithAuth } from "../../http";
import { GampCategory } from "./gampDictionary";
import { ObjectValidationStatus } from "../project/validationProcess/validationProcessApi";
import { StatusLevelAlertLevel, StatusLevelAlertStatus } from "../../app/components/StatusLevelAlert";

export type ComputerizedSystemCriticality = "HIGH" | "MEDIUM" | "LOW";


export interface ComputerizedSystemFunction {
    id: number;
    name: string;
    description: string | null;
}

export interface ComputerizedSystemProcess {
    id: number;
    name: string;
    description: string | null;
    computerizedSystemId: number;
    moduleId?: number | null;
    functions?: ComputerizedSystemFunction[];
}

export interface ComputerizedSystemModule {
    id: number;
    name: string;
    description: string | null;
    processes?: ComputerizedSystemProcess[];
}

export interface ComputerizedSystem {
    id: number
    name: string
    computerizedSystemIdentifier: string
    description?: string | null
    itOwnerId?: string | null
    department?: string | null
    status?: string | null
    isGxpRelevant: boolean
    criticality: ComputerizedSystemCriticality | null
    validationStatus?: ObjectValidationStatus | null
    validationDate?: Date | null
    nextReviewDate?: Date | null
    softwareVersion?: string | null
    vendor?: string | null
    supplierId: string | null
    supplierName: string | null
    backupDescription?: string | null
    structureId: AppId
    gampCategory: GampCategory
    createdBy: string
    createdByFullName: string
    createDate: Date,
    modules?: ComputerizedSystemModule[],
    processes?: ComputerizedSystemProcess[],
}

const qs = (params: Record<string, string | number | boolean | null | undefined>) => {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") sp.append(k, String(v));
    });
    const s = sp.toString();
    return s ? `?${s}` : "";
};

export const loadComputerizedSystems = createAsyncThunk<ComputerizedSystem[], { companyId?: number; search?: string, supplierId?: string } | void, AppThunkAPIType
>(
    "computerizedSystems/loadAll",
    async (args, { dispatch, rejectWithValue }) => {
        const query = qs({
            companyId: args && "companyId" in (args ?? {}) ? args.companyId : undefined,
            search: args && "search" in (args ?? {}) ? args.search : undefined,
            supplierId: args && "supplierId" in (args ?? {}) ? args.supplierId : undefined,
        });

        const result = await dispatch(getWithAuth({ url: `api/computerized-systems${query}` }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as ComputerizedSystem[];
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const loadComputerizedSystemById = createAsyncThunk<ComputerizedSystem, number, AppThunkAPIType
>(
    "computerizedSystems/loadById",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/computerized-systems/${id}` }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as ComputerizedSystem;
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const createComputerizedSystem = createAsyncThunk<
    ComputerizedSystem,
    Omit<ComputerizedSystem, "id">,
    AppThunkAPIType
>(
    "computerized-systems/create",
    async (payload, { rejectWithValue, dispatch }) => {
        const result = await dispatch(postWithAuth({
            url: "api/computerized-systems",
            payload
        }));
        const { payload: data } = result;

        if (postWithAuth.fulfilled.match(result)) {
            return data as ComputerizedSystem;
        } else {
            return rejectWithValue(data ?? { kind: "unknown" });
        }
    }
);

export const updateComputerizedSystem = createAsyncThunk<
    ComputerizedSystem,
    { id: number; data: ComputerizedSystem },
    AppThunkAPIType
>(
    "computerized-systems/update",
    async ({ id, data }, { rejectWithValue, dispatch }) => {
        const result = await dispatch(putWithAuth({
            url: `api/computerized-systems/${id}`,
            payload: data
        }));
        const { payload } = result;

        if (putWithAuth.fulfilled.match(result)) {
            return payload as ComputerizedSystem;
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export interface DeleteComputerizedSystemActionArgs {
    id: number,
    args: { password: string }
}

export const deleteComputerizedSystemViaPost = createAsyncThunk<
    number,
    DeleteComputerizedSystemActionArgs,
    AppThunkAPIType
>(
    "computerized-systems/deleteViaPost",
    async ({ id, args }, { rejectWithValue, dispatch }) => {
        const result = await dispatch(postWithAuth({
            url: `api/computerized-systems/${id}`,
            payload: args
        }));
        const { payload } = result;

        if (postWithAuth.fulfilled.match(result)) {
            return id;
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export type ComputerizedSystemBasicPatch = {
    id: number;
    name: string;
    computerizedSystemIdentifier: string;
    description: string | null;
    structureId: AppId;
    gampCategory: GampCategory | null;
};

export const updateComputerizedSystemBasicInfo = createAsyncThunk<
    ComputerizedSystem,
    ComputerizedSystemBasicPatch,
    AppThunkAPIType
>(
    "computerized-systems/updateBasicInfo",
    async ({ id, name, computerizedSystemIdentifier, description, structureId, gampCategory }, { dispatch, rejectWithValue }) => {
        const url = `api/computerized-systems/${id}/basic-info`;

        const result = await dispatch(putWithAuth({
            url,
            payload: { name, computerizedSystemIdentifier, description, structureId, gampCategory },
        }));

        if (putWithAuth.fulfilled.match(result)) {
            return result.payload as ComputerizedSystem;
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" });
        }
    }
);


export type ComputerizedSystemVendorPatch = {
    id: number;
    vendor: string;
    softwareVersion: string | null;
    supplierId: string | null;
};

export const updateComputerizedSystemVendorInfo = createAsyncThunk<
    ComputerizedSystem,
    ComputerizedSystemVendorPatch,
    AppThunkAPIType
>(
    "computerized-systems/updateVendordInfo",
    async ({ id, vendor, softwareVersion, supplierId }, { dispatch, rejectWithValue }) => {
        const url = `api/computerized-systems/${id}/vendor`;

        const result = await dispatch(putWithAuth({
            url,
            payload: { id, vendor, softwareVersion, supplierId },
        }));

        if (putWithAuth.fulfilled.match(result)) {
            return result.payload as ComputerizedSystem;
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" });
        }
    }
);

export type ComputerizedSystemUsagePatch = {
    id: number;
    department: string | null;
    itOwnerId: string | null;
    isGxpRelevant: boolean;
    criticality: ComputerizedSystemCriticality | null;
};

export const updateComputerizedSystemUsage = createAsyncThunk<
    ComputerizedSystem,
    ComputerizedSystemUsagePatch,
    AppThunkAPIType
>(
    "computerized-systems/updateUsageInfo",
    async ({ id, department, itOwnerId, isGxpRelevant, criticality }, { dispatch, rejectWithValue }) => {
        const url = `api/computerized-systems/${id}/usage`;

        const result = await dispatch(
            putWithAuth({
                url,
                payload: { id, department, itOwnerId, isGxpRelevant, criticality },
            })
        );

        if (putWithAuth.fulfilled.match(result)) {
            return result.payload as ComputerizedSystem;
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" });
        }
    }
);

export type ComputerizedSystemValidationPatch = {
    id: number;
    validationStatus: ObjectValidationStatus;
    validationDate: string | null;  
    nextReviewDate: string | null;   
};

export const updateComputerizedSystemValidation = createAsyncThunk<
    ComputerizedSystem,
    ComputerizedSystemValidationPatch,
    AppThunkAPIType
>(
    "computerized-systems/updateValidationInfo",
    async ({ id, validationStatus, validationDate, nextReviewDate }, { dispatch, rejectWithValue }) => {
        const url = `api/computerized-systems/${id}/validation`;

        const result = await dispatch(
            putWithAuth({
                url,
                payload: { id, validationStatus, validationDate, nextReviewDate },
            })
        );

        if (putWithAuth.fulfilled.match(result)) {
            return result.payload as ComputerizedSystem;
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" });
        }
    }
);

export const createSystemModule = createAsyncThunk<
    ComputerizedSystemModule,
    { systemId: number; module: Omit<ComputerizedSystemModule, "id"> },
    AppThunkAPIType
>(
    "computerized-systems/createModule",
    async ({ systemId, module }, { dispatch, rejectWithValue }) => {
        const url = `api/computerized-systems/${systemId}/modules`;

        const result = await dispatch(
            postWithAuth({
                url,
                payload: module,
            })
        );

        if (postWithAuth.fulfilled.match(result)) {
            return result.payload as ComputerizedSystemModule;
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" });
        }
    }
);

export const createSystemProcess = createAsyncThunk<
    ComputerizedSystemProcess,
    { systemId: number; moduleId: number; process: Omit<ComputerizedSystemProcess, "id" | "computerizedSystemId" | "moduleId"> },
    AppThunkAPIType
>(
    "computerized-systems/createProcess",
    async ({ systemId, moduleId, process }, { dispatch, rejectWithValue }) => {
        const url = `api/computerized-systems/modules/${moduleId}/processes`;

        const result = await dispatch(
            postWithAuth({
                url,
                payload: { ...process, computerizedSystemId: systemId, moduleId },
            })
        );

        if (postWithAuth.fulfilled.match(result)) {
            return result.payload as ComputerizedSystemProcess;
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" });
        }
    }
);

export const deleteSystemModule = createAsyncThunk<
    void,
    { moduleId: number },
    AppThunkAPIType
>(
    "computerized-systems/deleteModule",
    async ({ moduleId }, { dispatch, rejectWithValue }) => {
        const url = `api/computerized-systems/modules/${moduleId}`;

        const result = await dispatch(
            deleteWithAuth({ url })
        );

        if (deleteWithAuth.fulfilled.match(result)) {
            return;
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" });
        }
    }
);

export const deleteSystemProcess = createAsyncThunk<
    void,
    { processId: number },
    AppThunkAPIType
>(
    "computerized-systems/deleteProcess",
    async ({ processId }, { dispatch, rejectWithValue }) => {
        const url = `api/computerized-systems/processes/${processId}`;

        const result = await dispatch(
            deleteWithAuth({ url })
        );

        if (deleteWithAuth.fulfilled.match(result)) {
            return;
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" });
        }
    }
);

export const deleteSystemFunction = createAsyncThunk<
    void,
    { functionId: number },
    AppThunkAPIType
>(
    "computerized-systems/deleteFunction",
    async ({ functionId }, { dispatch, rejectWithValue }) => {
        const url = `api/computerized-systems/functions/${functionId}`;

        const result = await dispatch(
            deleteWithAuth({ url })
        );

        if (deleteWithAuth.fulfilled.match(result)) {
            return;
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" });
        }
    }
);

// Create Process directly under System (without module)
export const createSystemLevelProcess = createAsyncThunk<
    ComputerizedSystemProcess,
    { systemId: number; process: Omit<ComputerizedSystemProcess, "id" | "computerizedSystemId" | "moduleId"> },
    AppThunkAPIType
>(
    "computerized-systems/createSystemLevelProcess",
    async ({ systemId, process }, { dispatch, rejectWithValue }) => {
        const url = `api/computerized-systems/${systemId}/processes`;

        const result = await dispatch(
            postWithAuth({
                url,
                payload: { ...process, computerizedSystemId: systemId, moduleId: null },
            })
        );

        if (postWithAuth.fulfilled.match(result)) {
            return result.payload as ComputerizedSystemProcess;
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" });
        }
    }
);

export const createSystemFunction = createAsyncThunk<
    ComputerizedSystemFunction,
    { processId: number; func: Omit<ComputerizedSystemFunction, "id"> },
    AppThunkAPIType
>(
    "computerized-systems/createFunction",
    async ({ processId, func }, { dispatch, rejectWithValue }) => {
        const url = `api/computerized-systems/processes/${processId}/functions`;

        const result = await dispatch(
            postWithAuth({
                url,
                payload: func,
            })
        );

        if (postWithAuth.fulfilled.match(result)) {
            return result.payload as ComputerizedSystemFunction;
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" });
        }
    }
);

export const updateSystemModule = createAsyncThunk<
    ComputerizedSystemModule,
    { moduleId: number; module: Omit<ComputerizedSystemModule, 'id'> },
    AppThunkAPIType
>(
    'computerized-systems/updateModule',
    async ({ moduleId, module }, { dispatch, rejectWithValue }) => {
        const url = `api/computerized-systems/modules/${moduleId}`;

        const result = await dispatch(
            putWithAuth({
                url,
                payload: module,
            })
        );

        if (putWithAuth.fulfilled.match(result)) {
            return result.payload as ComputerizedSystemModule;
        } else {
            return rejectWithValue(result.payload ?? { kind: 'unknown' });
        }
    }
);

export const updateSystemProcess = createAsyncThunk<
    ComputerizedSystemProcess,
    { processId: number; process: Omit<ComputerizedSystemProcess, 'id'> },
    AppThunkAPIType
>(
    'computerized-systems/updateProcess',
    async ({ processId, process }, { dispatch, rejectWithValue }) => {
        const url = `api/computerized-systems/processes/${processId}`;

        const result = await dispatch(
            putWithAuth({
                url,
                payload: process,
            })
        );

        if (putWithAuth.fulfilled.match(result)) {
            return result.payload as ComputerizedSystemProcess;
        } else {
            return rejectWithValue(result.payload ?? { kind: 'unknown' });
        }
    }
);

export const updateSystemFunction = createAsyncThunk<
    ComputerizedSystemFunction,
    { functionId: number; func: Omit<ComputerizedSystemFunction, 'id'> },
    AppThunkAPIType
>(
    'computerized-systems/updateFunction',
    async ({ functionId, func }, { dispatch, rejectWithValue }) => {
        const url = `api/computerized-systems/functions/${functionId}`;

        const result = await dispatch(
            putWithAuth({
                url,
                payload: func,
            })
        );

        if (putWithAuth.fulfilled.match(result)) {
            return result.payload as ComputerizedSystemFunction;
        } else {
            return rejectWithValue(result.payload ?? { kind: 'unknown' });
        }
    }
);

export interface CheckSerialNoPayload {
    computerizedSystemId?: number;
    computerizedSystemIdentifier: string;
    structureId: number;
    supplierId: number;
}

export interface CheckSerialNoResponse {
    status: StatusLevelAlertStatus;
    level: StatusLevelAlertLevel;
}

export const checkSerialNo = createAsyncThunk<CheckSerialNoResponse, CheckSerialNoPayload, AppThunkAPIType>(
    "computerized-systems/check-serial-no",
    async (data, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/computerized-systems/exists`,
            payload: data,
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as CheckSerialNoResponse
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)